/**
 * Structural Event Tracking — delegated listeners for common interactions.
 *
 * All listeners use event delegation on document so they survive
 * Swup content swaps without re-binding.
 */

import { pushEvent } from './datalayer.js';

/**
 * CTA click tracking.
 * Fires cta_click on any element with [data-track-cta].
 */
function initCtaTracking() {
    document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-track-cta]');
        if (!target) return;

        pushEvent('cta_click', {
            text: target.textContent?.trim() || '',
            href: target.href || target.closest('a')?.href || '',
        });
    });
}

/**
 * Outbound link tracking.
 * Fires outbound_click on any <a> pointing to a different hostname.
 *
 * @param {string[]} excludedDomains — Hostnames to exclude (e.g., subdomains)
 */
function initOutboundTracking(excludedDomains = []) {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;

        try {
            const url = new URL(link.href, location.origin);

            // tel:, mailto:, sms: and friends parse with an EMPTY hostname, so the
            // comparison below never matches and every one of them would count as
            // an outbound click. They are enquiries, not exits — the opposite of
            // what outbound_click means. initContactLinkTracking owns tel:/mailto:.
            if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

            if (url.hostname === location.hostname) return;
            if (excludedDomains.includes(url.hostname)) return;

            pushEvent('outbound_click', {
                href: link.href,
                text: link.textContent?.trim() || '',
            });
        } catch {
            // Invalid URL — skip tracking
        }
    });
}

/**
 * Scroll depth tracking.
 * Fires scroll_depth at 25%, 50%, 75%, 100% milestones, once each per page.
 * Resets on Swup content:replace.
 *
 * @param {import('swup').default} swup — Swup instance for reset hook
 */
function initScrollTracking(swup) {
    const milestones = [25, 50, 75, 100];
    let fired = new Set();
    let ticking = false;

    function checkScroll() {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) return;

        const percent = Math.round((window.scrollY / scrollHeight) * 100);

        for (const milestone of milestones) {
            if (percent >= milestone && !fired.has(milestone)) {
                fired.add(milestone);
                pushEvent('scroll_depth', { percent: milestone });
            }
        }

        ticking = false;
    }

    window.addEventListener('scroll', () => {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(checkScroll);
        }
    }, { passive: true });

    // Reset milestones on Swup navigation
    swup.hooks.on('content:replace', () => {
        fired = new Set();
    });
}

/**
 * Element visibility tracking.
 * Fires element_view when [data-track-view] elements enter the viewport (50%).
 * Re-observes after Swup content:replace.
 *
 * @param {import('swup').default} swup — Swup instance for re-observe hook
 */
function initVisibilityTracking(swup) {
    let observer;

    function observe() {
        if (observer) observer.disconnect();

        observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        pushEvent('element_view', {
                            label: el.dataset.trackLabel || el.tagName.toLowerCase(),
                            tagName: el.tagName.toLowerCase(),
                        });
                        observer.unobserve(el);
                    }
                }
            },
            { threshold: 0.5 }
        );

        document.querySelectorAll('[data-track-view]').forEach((el) => {
            observer.observe(el);
        });
    }

    observe();

    // Re-observe on Swup navigation
    swup.hooks.on('content:replace', () => {
        observe();
    });
}

/**
 * Form submission tracking.
 * Fires form_submit on any form with [data-track-form].
 */
function initFormTracking() {
    document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-track-form]');
        if (!form) return;

        pushEvent('form_submit', {
            action: form.action || '',
            label: form.dataset.trackLabel || form.id || '',
        });
    });
}

/**
 * Phone and email click tracking.
 *
 * The counterpart to the protocol guard in initOutboundTracking: these clicks
 * were previously captured, wrongly, as outbound_click. Guarding alone would
 * take the site from bad data to no data on what is, for most sites built on
 * this kit, the primary conversion — so the signal is kept and named correctly.
 *
 * Reported as contact_click with a `method`, deliberately NOT as a conversion
 * event: it is intent, not a confirmed enquiry, and analytics can never see the
 * call itself. `placement` is optional — mark a container with
 * [data-track-placement="footer"] to learn WHERE enquiries start; without one it
 * is simply empty.
 */
function initContactLinkTracking() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="tel:"], a[href^="mailto:"]');
        if (!link) return;

        pushEvent('contact_click', {
            method: link.getAttribute('href').startsWith('tel:') ? 'phone' : 'email',
            placement: link.closest('[data-track-placement]')?.dataset.trackPlacement || '',
        });
    });
}

/**
 * Initialise all structural event tracking.
 *
 * @param {import('swup').default} swup — Swup router instance
 * @param {Object} [options={}]
 * @param {string[]} [options.excludedDomains=[]] — Hostnames to exclude from outbound tracking
 */
export function initEventTracking(swup, options = {}) {
    initCtaTracking();
    initOutboundTracking(options.excludedDomains || []);
    initScrollTracking(swup);
    initVisibilityTracking(swup);
    initFormTracking();
    initContactLinkTracking();
}
